// AGC: Machinery multi-select — recommendation chips, category filter tabs, risk badges
import React, { useState } from 'react'
import { Check, Plus, ChevronDown, Wrench } from 'lucide-react'
import { useTranslation } from '../../../hooks/useTranslation'
import type { VoiceFieldProps } from './types'

// AGC: Machinery suggestion shape from backend
interface MachinerySuggestion {
  value: string
  label: string
  machineCategory?: string
  maintenanceDue?: boolean
  hoursRemaining?: number
  confidence?: number
  isRelevant?: boolean
}

// AGC: Selected machinery item shape (JSON-serialized in inputValues.selectedMachinery)
interface SelectedMachine {
  id: string
  name: string
  category: string
}

// AGC: Category filter tab shape from backend
interface CategoryFilter {
  key: string
  label: string
  isRelevant: boolean
  count: number
}

export const MachinerySelector: React.FC<VoiceFieldProps> = ({
  info,
  inputValues,
  setInputValues,
  lastResponse,
  wizardMode,
}) => {
  const { t } = useTranslation()
  // AGC: Toggle between compact chips view and full category-filtered list
  const [expandedMachinery, setExpandedMachinery] = useState(false)

  const allMachines = (info.suggestions || []).filter((s: MachinerySuggestion) => s.value !== 'none')
  const selectedTaskCategory = inputValues.category || String(lastResponse?.parsedData?.category || '')
  const categoryMappings: Record<string, string[]> = (typeof info.categoryMappings === 'object' && info.categoryMappings !== null) ? info.categoryMappings : {}
  // AGC: Guard against empty string index — only look up when there's an actual category
  const relevantMachineTypes = selectedTaskCategory ? (categoryMappings[selectedTaskCategory] ?? null) : null

  // AGC: Get top recommended machines (relevant to task category, sorted by confidence)
  const recommended: MachinerySuggestion[] = relevantMachineTypes
    ? allMachines.filter((m: MachinerySuggestion) => relevantMachineTypes.includes(m.machineCategory || '')).slice(0, 3)
    : allMachines.slice(0, 3)

  // AGC: Parse selected machinery from JSON
  let selectedMachinery: SelectedMachine[] = []
  try { selectedMachinery = JSON.parse(inputValues.selectedMachinery || '[]') } catch { /* empty */ }

  const toggleMachine = (m: MachinerySuggestion) => {
    let updated: SelectedMachine[]
    const isSelected = selectedMachinery.some(s => s.id === m.value)
    if (isSelected) {
      updated = selectedMachinery.filter(s => s.id !== m.value)
    } else {
      updated = [...selectedMachinery.filter(s => s.id !== 'none'), {
        id: m.value,
        name: m.label.replace(/^🚜\s*/, '').replace(/^➖\s*/, ''),
        category: m.machineCategory || '',
      }]
    }
    setInputValues(prev => ({ ...prev, selectedMachinery: JSON.stringify(updated) }))
  }

  // AGC: Sizing classes — bigger in wizard mode to fill the full-screen card layout
  const sz = wizardMode
    ? { card: 'p-5', label: 'text-base', chip: 'px-4 py-2.5 text-base', checkIcon: 'w-5 h-5' }
    : { card: 'p-2', label: 'text-xs', chip: 'px-2.5 py-1 text-xs', checkIcon: 'w-3 h-3' }

  return (
    <div className={`${sz.card} border rounded-lg ${
      info.isRequired
        ? 'bg-orange-500/5 border-orange-500/30'
        : 'bg-white/5 border-white/20'
    }`}>
      <div className="mb-1.5">
        <span className={`${sz.label} font-medium flex items-center gap-1 ${
          info.isRequired ? 'text-orange-300' : 'text-white/80'
        }`}>
          🚜 {info.fieldLabel}
        </span>
        {info.reason && (
          <p className="text-white/40 text-xs mt-0.5">{info.reason}</p>
        )}
      </div>

      {info.noMachinesRegistered ? (
        /* AGC: No machines registered warning */
        <div className="text-center py-2">
          <p className="text-orange-300/80 text-xs">
            {t('voice.noMachinesRegistered')}
          </p>
          <p className="text-white/40 text-xs mt-1">
            {t('voice.registerMachineFirst')}
          </p>
        </div>
      ) : (
        <>
          {/* AGC: Phase 8B — Recommendation chips (compact view) */}
          {/* AGC: Shows top 3 recommended + "Nenhum" as quick-select toggle chips */}
          {recommended.length > 0 && (
            <div className="mb-2">
              <div className="flex flex-wrap gap-1.5">
                {/* AGC: "Nenhum" chip — always visible */}
                <button
                  onClick={() => setInputValues(prev => ({ ...prev, selectedMachinery: 'none' }))}
                  className={`inline-flex items-center gap-1 ${sz.chip} rounded-full transition-colors ${
                    inputValues.selectedMachinery === 'none'
                      ? 'bg-white/20 border border-white/40 text-white font-medium'
                      : 'bg-white/5 border border-white/15 text-white/60 hover:bg-white/10'
                  }`}
                >
                  ➖ {t('voice.noMachine')}
                </button>

                {/* AGC: Top 3 recommendation chips with risk badges */}
                {recommended.map((m) => {
                  const isSelected = selectedMachinery.some(s => s.id === m.value)
                  return (
                    <button
                      key={m.value}
                      onClick={() => toggleMachine(m)}
                      className={`inline-flex items-center gap-1 ${sz.chip} rounded-full transition-colors ${
                        isSelected
                          ? 'bg-emerald-500/25 border border-emerald-500/50 text-emerald-300 font-medium'
                          : 'bg-emerald-500/8 border border-emerald-500/20 text-emerald-200/80 hover:bg-emerald-500/15'
                      }`}
                    >
                      {isSelected && <Check className={sz.checkIcon} />}
                      <span className="truncate max-w-[120px]">{m.label.replace(/^🚜\s*/, '')}</span>
                      {/* AGC: Risk badge — wrench icon for maintenance due */}
                      {m.maintenanceDue && (
                        <span title={t('voice.maintenanceDue')}><Wrench className="w-3 h-3 text-yellow-400 flex-shrink-0" /></span>
                      )}
                    </button>
                  )
                })}

                {/* AGC: "+Mais" expand button to show full list */}
                {allMachines.length > 3 && (
                  <button
                    onClick={() => setExpandedMachinery(prev => !prev)}
                    className="inline-flex items-center gap-0.5 px-2 py-1 rounded-full text-xs text-white/50 hover:text-white/80 bg-white/5 hover:bg-white/10 transition-colors"
                  >
                    <Plus className="w-3 h-3" />
                    {t('voice.moreOptions')} ({allMachines.length - 3})
                    <ChevronDown className={`w-3 h-3 transition-transform ${expandedMachinery ? 'rotate-180' : ''}`} />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* AGC: Expanded full list — shown when "+Mais" clicked or no category context */}
          {(expandedMachinery || !selectedTaskCategory) && (
            <>
              {/* AGC: Category filter tabs - horizontal scrollable */}
              <div className="flex gap-1 overflow-x-auto pb-1 mb-1.5 scrollbar-thin">
                {/* AGC: Recommended tab - uses backend-computed confidence scores */}
                {(() => {
                  const recommendedCount = allMachines.filter((m: MachinerySuggestion) => (m.confidence || 0) >= 80).length
                  return recommendedCount > 0 ? (
                    <button
                      onClick={() => setInputValues(prev => ({ ...prev, _machineryCategoryFilter: '_recommended' }))}
                      className={`flex-shrink-0 px-2 py-1 rounded text-xs transition-colors whitespace-nowrap ${
                        inputValues._machineryCategoryFilter === '_recommended'
                          ? 'bg-emerald-500/30 text-emerald-300 font-medium'
                          : 'bg-emerald-500/10 text-emerald-300/80 hover:bg-emerald-500/20'
                      }`}
                    >
                      ⭐ {t('voice.recommended')} ({recommendedCount})
                    </button>
                  ) : null
                })()}

                {/* AGC: All machines tab */}
                <button
                  onClick={() => setInputValues(prev => ({ ...prev, _machineryCategoryFilter: '' }))}
                  className={`flex-shrink-0 px-2 py-1 rounded text-xs transition-colors ${
                    !inputValues._machineryCategoryFilter || inputValues._machineryCategoryFilter === ''
                      ? 'bg-emerald-500/30 text-emerald-300 font-medium'
                      : 'bg-white/5 text-white/60 hover:bg-white/10'
                  }`}
                >
                  {t('voice.allCategories')}
                </button>

                {/* AGC: Individual category tabs - highlight relevant ones from backend scoring */}
                {(info.categoryFilters || []).map((cat: CategoryFilter) => {
                  const isDynamicallyRelevant = cat.isRelevant
                  return (
                    <button
                      key={cat.key}
                      onClick={() => setInputValues(prev => ({ ...prev, _machineryCategoryFilter: cat.key }))}
                      className={`flex-shrink-0 px-2 py-1 rounded text-xs transition-colors whitespace-nowrap ${
                        inputValues._machineryCategoryFilter === cat.key
                          ? 'bg-emerald-500/30 text-emerald-300 font-medium'
                          : isDynamicallyRelevant && selectedTaskCategory
                            ? 'bg-emerald-500/10 text-emerald-300/80 hover:bg-emerald-500/20'
                            : 'bg-white/5 text-white/60 hover:bg-white/10'
                      }`}
                    >
                      {cat.label} ({cat.count})
                    </button>
                  )
                })}
              </div>

              {/* AGC: Multi-select machinery list with checkboxes - filtered by category */}
              <div className="space-y-1 max-h-40 overflow-y-auto mb-1.5">
                {(() => {
                  const allSuggestions: MachinerySuggestion[] = info.suggestions || []
                  const currentFilter = inputValues._machineryCategoryFilter || ''

                  const filteredMachines = allSuggestions.filter((s) => {
                    if (s.value === 'none') return true
                    if (currentFilter === '_recommended') {
                      // AGC: Backend already scored: 95=brand/transcript, 85=category-relevant, 40=other
                      return (s.confidence || 0) >= 80
                    }
                    if (currentFilter === '') return true
                    return s.machineCategory === currentFilter
                  })

                  return filteredMachines.map((s) => {
                    // AGC: Re-parse inside map to get fresh state
                    let currentSelected: SelectedMachine[] = []
                    try { currentSelected = JSON.parse(inputValues.selectedMachinery || '[]') } catch { /* empty */ }
                    const isSelected = s.value === 'none'
                      ? currentSelected.length === 0 && inputValues.selectedMachinery === 'none'
                      : currentSelected.some(m => m.id === s.value)
                    // AGC: Dynamic relevance based on current task category
                    const isDynamicallyRelevant = !relevantMachineTypes || relevantMachineTypes.includes(s.machineCategory || '')

                    return (
                      <div
                        key={s.value}
                        className={`flex items-center gap-1.5 px-2 py-1.5 rounded text-xs transition-colors ${
                          isSelected
                            ? 'bg-emerald-500/20 border border-emerald-500/40'
                            : isDynamicallyRelevant && selectedTaskCategory
                              ? 'bg-emerald-500/5 hover:bg-emerald-500/10 border border-emerald-500/20'
                              : 'bg-white/5 hover:bg-white/10 border border-transparent'
                        }`}
                      >
                        <button
                          onClick={() => {
                            if (s.value === 'none') {
                              // AGC: Clear all selections
                              setInputValues(prev => ({ ...prev, selectedMachinery: 'none' }))
                            } else {
                              toggleMachine(s)
                            }
                          }}
                          className="flex-shrink-0"
                        >
                          {isSelected ? (
                            <Check className="w-3.5 h-3.5 text-emerald-400" />
                          ) : (
                            <div className="w-3.5 h-3.5 rounded border border-white/30" />
                          )}
                        </button>
                        <span className={`flex-1 truncate ${
                          isSelected
                            ? 'text-emerald-300'
                            : isDynamicallyRelevant && selectedTaskCategory
                              ? 'text-emerald-200/80'
                              : 'text-white/80'
                        }`}>
                          {s.label}
                        </span>
                        {/* AGC: Phase 8B — Risk badge for maintenance due */}
                        {s.maintenanceDue && (
                          <span title={t('voice.maintenanceDue')}><Wrench className="w-3 h-3 text-yellow-400 flex-shrink-0" /></span>
                        )}
                      </div>
                    )
                  })
                })()}
              </div>
            </>
          )}
        </>
      )}

      {/* AGC: Selected machines summary — ALWAYS visible even when list is collapsed */}
      {(() => {
        let selected: SelectedMachine[] = []
        try { selected = JSON.parse(inputValues.selectedMachinery || '[]') } catch { /* empty */ }
        if (inputValues.selectedMachinery === 'none' || selected.length === 0) return null
        return (
          <div className="pt-1.5 border-t border-white/10">
            <span className="text-emerald-300 text-[10px] font-medium">
              ✅ {selected.length} {t('voice.machinesSelected')}:
            </span>
            <div className="mt-0.5 flex flex-wrap gap-1">
              {selected.map((m) => (
                <span key={m.id} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-emerald-500/20 rounded text-emerald-300 text-[10px]">
                  🚜 {m.name}
                  <button
                    onClick={() => {
                      const upd = selected.filter(x => x.id !== m.id)
                      setInputValues(prev => ({ ...prev, selectedMachinery: JSON.stringify(upd) }))
                    }}
                    className="ml-0.5 text-emerald-300/60 hover:text-red-400"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
        )
      })()}
    </div>
  )
}
